import React, { useState } from 'react';

function ARBar({ lt60, d60_90, d90_180, d180_365, gt365 }) {
  const total = lt60 + d60_90 + d90_180 + d180_365 + Math.max(0, gt365);
  if (total === 0) return null;
  const pct = (v) => `${Math.round(Math.max(0, v) / total * 100)}%`;
  return (
    <div className="ar-bar">
      {lt60 > 0 && <div style={{ width: pct(lt60), background: '#10b981' }} title={`<60d: AED ${lt60.toLocaleString()}`} />}
      {d60_90 > 0 && <div style={{ width: pct(d60_90), background: '#f59e0b' }} title={`60-90d: AED ${d60_90.toLocaleString()}`} />}
      {d90_180 > 0 && <div style={{ width: pct(d90_180), background: '#ef4444' }} title={`90-180d: AED ${d90_180.toLocaleString()}`} />}
      {d180_365 > 0 && <div style={{ width: pct(d180_365), background: '#991b1b' }} title={`180-365d: AED ${d180_365.toLocaleString()}`} />}
      {gt365 > 0 && <div style={{ width: pct(gt365), background: '#450a0a' }} title={`>365d: AED ${gt365.toLocaleString()}`} />}
    </div>
  );
}

export default function Collections({ ar, jobs, clientMaster, entity }) {
  const [sortBy, setSortBy] = useState('critical');
  const [search, setSearch] = useState('');

  // Enrich AR with client master and open jobs
  const openJobsByClient = {};
  jobs.filter(j => j.status === 'Initiated').forEach(j => {
    openJobsByClient[j.client] = (openJobsByClient[j.client] || 0) + 1;
  });

  const enriched = ar.map(row => {
    const master = clientMaster[row.client] || {};
    const overdue = row.d60_90 + row.d90_180 + row.d180_365 + Math.max(0, row.gt365);
    const critical = row.d90_180 + row.d180_365 + Math.max(0, row.gt365);
    const openJobs = openJobsByClient[row.client] || 0;
    const riskScore = critical * 2 + overdue + (openJobs > 0 && overdue > 0 ? 50000 : 0);
    return {
      ...row,
      responsible: master.responsible || '—',
      overdue,
      critical,
      openJobs,
      riskScore,
      combinedFlag: openJobs > 0 && critical > 0,
    };
  });

  const filtered = enriched.filter(r => {
    if (search && !r.client.toLowerCase().includes(search.toLowerCase())) return false;
    return r.pending > 0;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'critical') return b.riskScore - a.riskScore;
    if (sortBy === 'total') return b.pending - a.pending;
    if (sortBy === 'overdue') return b.overdue - a.overdue;
    if (sortBy === 'client') return a.client.localeCompare(b.client);
    return 0;
  });

  // Summary stats
  const totalAR = filtered.reduce((s, r) => s + r.pending, 0);
  const totalOverdue = filtered.reduce((s, r) => s + r.overdue, 0);
  const totalCritical = filtered.reduce((s, r) => s + r.critical, 0);
  const combinedFlags = filtered.filter(r => r.combinedFlag).length;

  return (
    <div className="view collections">
      <div className="kpi-row">
        <div className="kpi">
          <span className="kpi-value">AED {(totalAR/1000).toFixed(0)}K</span>
          <span className="kpi-label">Total outstanding AR</span>
        </div>
        <div className={`kpi ${totalOverdue > 0 ? 'warn' : ''}`}>
          <span className="kpi-value">AED {(totalOverdue/1000).toFixed(0)}K</span>
          <span className="kpi-label">Overdue 60+ days</span>
          <span className="kpi-sub">{Math.round(totalOverdue/totalAR*100)||0}% of total</span>
        </div>
        <div className={`kpi ${totalCritical > 0 ? 'danger' : ''}`}>
          <span className="kpi-value">AED {(totalCritical/1000).toFixed(0)}K</span>
          <span className="kpi-label">Critical 90+ days 🔴</span>
        </div>
        {combinedFlags > 0 && (
          <div className="kpi danger">
            <span className="kpi-value">{combinedFlags}</span>
            <span className="kpi-label">Active work + overdue AR 🔴</span>
            <span className="kpi-sub">Stop work or get commitment</span>
          </div>
        )}
      </div>

      <div className="table-controls">
        <input
          className="search-input"
          placeholder="Search client..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div className="sort-pills">
          <span className="sort-label">Sort by:</span>
          {[
            { id: 'critical', label: 'Risk' },
            { id: 'total', label: 'Total' },
            { id: 'overdue', label: 'Overdue' },
            { id: 'client', label: 'Client' },
          ].map(s => (
            <button key={s.id} className={`pill small ${sortBy===s.id?'active':''}`} onClick={() => setSortBy(s.id)}>{s.label}</button>
          ))}
        </div>
      </div>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Owner</th>
              <th className="right">Total AR</th>
              <th className="right">&lt;60d</th>
              <th className="right">60-90d</th>
              <th className="right">90-180d</th>
              <th className="right">180-365d</th>
              <th className="right">&gt;365d</th>
              <th>Distribution</th>
              <th>Flag</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, i) => (
              <tr key={i} className={row.combinedFlag ? 'row-critical' : row.critical > 0 ? 'row-danger' : row.overdue > 0 ? 'row-warn' : ''}>
                <td className="client-cell">{row.client}</td>
                <td className="owner-cell">{row.responsible}</td>
                <td className="right bold">AED {row.pending.toLocaleString('en',{maximumFractionDigits:0})}</td>
                <td className={`right ${row.lt60 > 0 ? 'good' : ''}`}>{row.lt60 > 0 ? row.lt60.toLocaleString('en',{maximumFractionDigits:0}) : '—'}</td>
                <td className={`right ${row.d60_90 > 0 ? 'warn' : ''}`}>{row.d60_90 > 0 ? row.d60_90.toLocaleString('en',{maximumFractionDigits:0}) : '—'}</td>
                <td className={`right ${row.d90_180 > 0 ? 'danger' : ''}`}>{row.d90_180 > 0 ? row.d90_180.toLocaleString('en',{maximumFractionDigits:0}) : '—'}</td>
                <td className={`right ${row.d180_365 > 0 ? 'danger' : ''}`}>{row.d180_365 > 0 ? row.d180_365.toLocaleString('en',{maximumFractionDigits:0}) : '—'}</td>
                <td className={`right ${row.gt365 > 0 ? 'danger' : ''}`}>{row.gt365 > 0 ? row.gt365.toLocaleString('en',{maximumFractionDigits:0}) : '—'}</td>
                <td><ARBar {...row} /></td>
                <td>
                  {row.combinedFlag && <span className="flag-pill red">Active jobs + overdue</span>}
                  {!row.combinedFlag && row.critical > 0 && <span className="flag-pill red">90+ days</span>}
                  {!row.combinedFlag && !row.critical && row.overdue > 0 && <span className="flag-pill amber">Overdue</span>}
                  {row.openJobs > 0 && !row.combinedFlag && <span className="flag-pill blue">{row.openJobs} open jobs</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
